// src/pages/LMS/Enrollments/EnrollmentList.js
// UPDATED VERSION WITH PROPER RE-ENROLL

import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Button, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextField, InputAdornment,
  Dialog, DialogTitle, DialogContent, DialogActions, FormControl,
  InputLabel, Select, MenuItem, Chip, CircularProgress, Pagination,
  IconButton, Menu, LinearProgress, Avatar, Stack, Tooltip, Alert, Snackbar
} from '@mui/material';
import {
  Search as SearchIcon,
  MoreVert as MoreVertIcon,
  PersonAdd as EnrollIcon,
  Download as DownloadIcon,
  School as CourseIcon,
  TrendingUp as ProgressIcon,
  Assessment as AnalyticsIcon,
  ArrowBack as BackIcon,
  History as HistoryIcon,
  PersonOff as UnenrollIcon,
  PersonAddAlt as ReenrollIcon
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import axiosInstance from '../../../utils/axiosInstance';

const EnrollmentList = () => {
  const { chatbotId } = useParams();
  const navigate = useNavigate();

  const [enrollments, setEnrollments] = useState([]);
  const [courses, setCourses] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [courseFilter, setCourseFilter] = useState('');
  
  const [bulkEnrollDialog, setBulkEnrollDialog] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState('');
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [selectedEnrollment, setSelectedEnrollment] = useState(null);
  
  // Snackbar for notifications
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    loadEnrollments();
    loadCourses();
    loadStudents();
  }, [page, statusFilter, courseFilter, searchTerm]);

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const loadEnrollments = async () => {
    setLoading(true);
    try {
      const params = { page, per_page: 20 };
      if (statusFilter) params.status = statusFilter;
      if (courseFilter) params.course_id = courseFilter;
      if (searchTerm) params.search = searchTerm;
      if (chatbotId) params.chatbot_id = chatbotId;
      const response = await axiosInstance.get('/api/lms/enrollments', { params });
      setEnrollments(response.data.data);
      setTotalPages(response.data.pagination.total_pages);
    } catch (error) {
      console.error('Error loading enrollments:', error);
      showSnackbar('Failed to load enrollments', 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadCourses = async () => {
    try {
      const response = await axiosInstance.get('/api/lms/courses', {
        params: { status: 'published', per_page: 100,  chatbot_id: chatbotId }
      });
      setCourses(response.data.data);
    } catch (error) {
      console.error('Error loading courses:', error);
    }
  };

  const loadStudents = async () => {
    try {
      const response = await axiosInstance.get('/api/lms/students', {
        params: { status: 'active', per_page: 100 ,  chatbot_id: chatbotId}
      });
      setStudents(response.data.data);
    } catch (error) {
      console.error('Error loading students:', error);
    }
  };

  const handleEnrollStudent = async () => {
    if (!selectedCourse || selectedStudents.length === 0) return;

    try {
      const response = await axiosInstance.post(`/api/lms/courses/${selectedCourse}/bulk-enroll`, {
        student_ids_or_identities: selectedStudents
      });
      
      setBulkEnrollDialog(false);
      setSelectedCourse('');
      setSelectedStudents([]);
      loadEnrollments();
      showSnackbar(`Successfully enrolled ${selectedStudents.length} student(s)`, 'success');
    } catch (error) {
      console.error('Error enrolling students:', error);
      showSnackbar(error.response?.data?.message || 'Failed to enroll students', 'error');
    }
  };

  const handleUnenroll = async (enrollmentId) => {
    if (!window.confirm('Are you sure you want to unenroll this student?')) return;

    try {
      await axiosInstance.post(`/api/lms/enrollments/${enrollmentId}/unenroll`);
      loadEnrollments();
      showSnackbar('Student unenrolled successfully', 'success');
    } catch (error) {
      console.error('Error unenrolling student:', error);
      showSnackbar(error.response?.data?.message || 'Failed to unenroll student', 'error');
    }
  };

  const handleReenroll = async (enrollment) => {
    const studentName = `${enrollment.student?.first_name || ''} ${enrollment.student?.last_name || ''}`.trim();
    
    if (!window.confirm(
      `Re-enroll ${studentName || 'this student'} in ${enrollment.course?.title}?\n\n` +
      `Their progress will be preserved and they can continue where they left off.`
    )) return;

    try {
      // Use dedicated re-enroll endpoint
      const response = await axiosInstance.post(
        `/api/lms/enrollments/${enrollment.id}/reenroll`
      );
      
      loadEnrollments();
      showSnackbar('Student re-enrolled successfully', 'success');
    } catch (error) {
      console.error('Error re-enrolling student:', error);
      showSnackbar(error.response?.data?.message || 'Failed to re-enroll student', 'error');
    }
  };

  const getStatusChip = (status) => {
    const colors = {
      enrolled: 'default',
      in_progress: 'info',
      completed: 'success',
      dropped: 'error'
    };
    return (
      <Chip
        label={status.replace('_', ' ').toUpperCase()}
        color={colors[status] || 'default'}
        size="small"
      />
    );
  };

  const renderActionButtons = (enrollment) => {
    const isDropped = enrollment.status === 'dropped';
    const isCompleted = enrollment.status === 'completed';
    const isActive = enrollment.status === 'enrolled' || enrollment.status === 'in_progress';

    return (
      <>
        {isDropped ? (
          // Dropped: Show "View Last Progress" + "Re-enroll"
          <>
            <Tooltip title="View Last Progress">
              <IconButton
                size="small"
                onClick={() => navigate(
                  `/chatbot/${chatbotId}/lms/enrollments/${enrollment.id}/progress`
                )}
              >
                <HistoryIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Re-enroll Student">
              <IconButton
                size="small"
                color="primary"
                onClick={() => handleReenroll(enrollment)}
              >
                <ReenrollIcon />
              </IconButton>
            </Tooltip>
          </>
        ) : isCompleted ? (
          // Completed: Show "View Progress" only
          <Tooltip title="View Progress">
            <IconButton
              size="small"
              onClick={() => navigate(
                `/chatbot/${chatbotId}/lms/enrollments/${enrollment.id}/progress`
              )}
            >
              <ProgressIcon />
            </IconButton>
          </Tooltip>
        ) : (
          // Active (Enrolled/In Progress): Show "View Progress" + Menu
          <>
            <Tooltip title="View Progress">
              <IconButton
                size="small"
                onClick={() => navigate(
                  `/chatbot/${chatbotId}/lms/enrollments/${enrollment.id}/progress`
                )}
              >
                <ProgressIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="More Actions">
              <IconButton
                size="small"
                onClick={(e) => {
                  setMenuAnchor(e.currentTarget);
                  setSelectedEnrollment(enrollment);
                }}
              >
                <MoreVertIcon />
              </IconButton>
            </Tooltip>
          </>
        )}
      </>
    );
  };

  return (
    <Box sx={{ p: 3, mt: 10 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3, alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Button
            startIcon={<BackIcon />}
            onClick={() => navigate(`/chatbot/${chatbotId}/lms`)}
            sx={{ mr: 2 }}
          >
            Back
          </Button>
          <Typography variant="h4" fontWeight={700}>
            <CourseIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            Enrollments
          </Typography>
        </Box>
        <Stack direction="row" spacing={2}>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={() => {}}
          >
            Export
          </Button>
          <Button
            variant="contained"
            startIcon={<EnrollIcon />}
            onClick={() => setBulkEnrollDialog(true)}
          >
            Bulk Enroll
          </Button>
        </Stack>
      </Box>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Stack direction="row" spacing={2}>
          <TextField
            size="small"
            placeholder="Search students..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>
            }}
            sx={{ flexGrow: 1 }}
          />
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              label="Status"
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="enrolled">Enrolled</MenuItem>
              <MenuItem value="in_progress">In Progress</MenuItem>
              <MenuItem value="completed">Completed</MenuItem>
              <MenuItem value="dropped">Dropped</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Course</InputLabel>
            <Select
              value={courseFilter}
              onChange={(e) => setCourseFilter(e.target.value)}
              label="Course"
            >
              <MenuItem value="">All Courses</MenuItem>
              {courses.map(course => (
                <MenuItem key={course.id} value={course.id}>{course.title}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </Paper>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
          <CircularProgress />
        </Box>
      ) : enrollments.length === 0 ? (
        <Paper sx={{ p: 5, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No enrollments found
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {statusFilter || courseFilter || searchTerm
              ? 'Try adjusting your filters'
              : 'Start by enrolling students in courses'}
          </Typography>
          <Button
            variant="contained"
            startIcon={<EnrollIcon />}
            onClick={() => setBulkEnrollDialog(true)}
          >
            Enroll Students
          </Button>
        </Paper>
      ) : (
        <>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Student</TableCell>
                  <TableCell>Course</TableCell>
                  <TableCell>Progress</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Tests Passed</TableCell>
                  <TableCell>Enrolled Date</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {enrollments.map((enrollment) => (
                  <TableRow 
                    key={enrollment.id} 
                    hover
                    sx={{ 
                      opacity: enrollment.status === 'dropped' ? 0.6 : 1,
                      bgcolor: enrollment.status === 'completed' ? 'success.light' : 'inherit'
                    }}
                  >
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Avatar sx={{ mr: 2, bgcolor: 'primary.main' }}>
                          {enrollment.student?.first_name?.[0] || enrollment.student?.name?.[0] || 'S'}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight={500}>
                            {enrollment.student?.name || 
                             `${enrollment.student?.first_name || ''} ${enrollment.student?.last_name || ''}`.trim() || 
                             'Unknown Student'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {enrollment.student?.email || ''}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {enrollment.course?.title || 'Unknown'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ width: 100 }}>
                        <Typography variant="caption">
                          {enrollment.progress_percentage?.toFixed(0) || 0}%
                        </Typography>
                        <LinearProgress
                          variant="determinate"
                          value={enrollment.progress_percentage || 0}
                          sx={{ height: 6, borderRadius: 3 }}
                        />
                      </Box>
                    </TableCell>
                    <TableCell>{getStatusChip(enrollment.status)}</TableCell>
                    <TableCell>
                      {enrollment.tests_passed || 0} / {enrollment.total_tests || 0}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {new Date(enrollment.enrolled_at).toLocaleDateString()}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                        {renderActionButtons(enrollment)}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={(e, newPage) => setPage(newPage)}
                color="primary"
              />
            </Box>
          )}
        </>
      )}

      {/* More Actions Menu - Only for Active Enrollments */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
      >
        <MenuItem onClick={() => {
          setMenuAnchor(null);
          navigate(`/chatbot/${chatbotId}/lms/enrollments/${selectedEnrollment?.id}/progress`);
        }}>
          <ProgressIcon sx={{ mr: 1 }} fontSize="small" />
          View Progress
        </MenuItem>
        <MenuItem 
          onClick={() => {
            setMenuAnchor(null);
            handleUnenroll(selectedEnrollment?.id);
          }}
          sx={{ color: 'error.main' }}
        >
          <UnenrollIcon sx={{ mr: 1 }} fontSize="small" />
          Unenroll Student
        </MenuItem>
      </Menu>

      {/* Bulk Enroll Dialog */}
      <Dialog open={bulkEnrollDialog} onClose={() => setBulkEnrollDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Bulk Enroll Students</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 2, mb: 2 }}>
            <InputLabel>Course</InputLabel>
            <Select
              value={selectedCourse}
              onChange={(e) => setSelectedCourse(e.target.value)}
              label="Course"
            >
              {courses.filter(c => c.status === 'published').map(course => (
                <MenuItem key={course.id} value={course.id}>{course.title}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel>Students</InputLabel>
            <Select
              multiple
              value={selectedStudents}
              onChange={(e) => setSelectedStudents(e.target.value)}
              label="Students"
              renderValue={(selected) => `${selected.length} student${selected.length !== 1 ? 's' : ''} selected`}
            >
              {students.map(student => (
                <MenuItem key={student.id} value={student.student_identity}>
                  {student.first_name} {student.last_name} ({student.email})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkEnrollDialog(false)}>Cancel</Button>
          <Button 
            variant="contained" 
            onClick={handleEnrollStudent}
            disabled={!selectedCourse || selectedStudents.length === 0}
          >
            Enroll {selectedStudents.length} Student{selectedStudents.length !== 1 ? 's' : ''}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default EnrollmentList;